// Recrutement — tableau de bord complet (port rec12-dash.jsx): hero
// personnalisé, 6 KPI compacts animés, « À traiter », entretiens à
// venir, santé du pipeline (funnel + mini-barres par demande), offres
// en cours, onboarding en cours, catalogue et journal d'audit.
//
// Deviations du proto (délibérées): les personas rh/mgr/own deviennent
// les vraies permissions (canEdit/canValidate/canSal); les « À traiter »
// sont calculés dans la page (RecrutementPage) car ils servent aussi
// aux badges de navigation.
import { useEffect, useState } from 'react';
import { ENTITIES, REC_STAGES, activityLabel } from '@pointage/shared';
import type {
  RecActivityDTO, RecApplicationDTO, RecInterviewDTO, RecOnboardingDTO,
  RecQueueItemDTO, RecRequisitionDTO, RecRoleDTO,
} from '../../api/hooks.js';
import {
  RecAv, RecEnt, RecSal, TPL_META, recDays, recFmtMoney, recOfferDaysLeft, recOfferOf, recOfferStOf,
} from './common.js';
import { obDays, obDocsMissing, obPct } from './Onboarding.js';
import { BarPill, DimMeter, fmt1 } from './V2Ui.js';
import { useRecSt } from './V2Sections.js';
import { ProbationStrip, cpState, obDay } from './Probation.js';

function entOf(id: string) {
  return ENTITIES.find((e) => e.id === id);
}

// ── animations du proto (compteurs + montée des barres) ─────
function useMounted() {
  const [m, setM] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setM(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return m;
}
function useCount(n: number) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf: number;
    const t0 = performance.now();
    const dur = 750;
    const step = (t: number) => {
      const p = Math.min(1, (t - t0) / dur);
      setV(Math.round(n * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [n]);
  return v;
}

function Kpi({ v, l, c, ic, i, onGo }: { v: number; l: string; c: string; ic: string; i: number; onGo?: () => void }) {
  const n = useCount(v);
  return (
    <button
      className="d11-kpi"
      style={{ '--kc': c, animationDelay: `${i * 60}ms`, cursor: onGo ? 'pointer' : 'default' } as React.CSSProperties}
      onClick={onGo}
    >
      <span className="ic">{ic}</span>
      <span className="tx"><b>{n}</b><span className="l">{l}</span></span>
      {onGo && <span className="go">→</span>}
    </button>
  );
}

function Ring({ pct, c }: { pct: number; c: string }) {
  const m = useMounted();
  const C = 2 * Math.PI * 15;
  return (
    <span className="d11-ring" style={{ '--rc': c } as React.CSSProperties}>
      <svg width="38" height="38">
        <circle className="bgc" cx="19" cy="19" r="15"></circle>
        <circle className="fgc" cx="19" cy="19" r="15" strokeDasharray={C} strokeDashoffset={C * (1 - (m ? pct : 0) / 100)}></circle>
      </svg>
      <b>{pct}%</b>
    </span>
  );
}

function Sec({ ic, c, t, k, sub, more, onMore, sp, d, children }: {
  ic: string; c: string; t: string; k?: number; sub?: string;
  more?: string; onMore?: () => void; sp: number; d: number; children: React.ReactNode;
}) {
  return (
    <section className="d11-sec" style={{ '--sp': sp, '--sc': c, animationDelay: `${d}ms` } as React.CSSProperties}>
      <div className="d11-sh">
        <span className="gl">{ic}</span>
        <div style={{ minWidth: 0 }}>
          <h3>{t}{k != null && <span className="k">{k}</span>}</h3>
          {sub && <div className="sub">{sub}</div>}
        </div>
        {more && <button className="d11-more" onClick={onMore}>{more} →</button>}
      </div>
      <div className="d11-body">{children}</div>
    </section>
  );
}

const MO = ['janv', 'févr', 'mars', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc'];
const GLYPH: Record<string, string> = { 'Créa': '🎬', 'Tech': '💻', 'Commercial': '🤝' };

// ── libellés du journal d'audit ─────────────────────────────
// Le builder partagé porte désormais TOUTES les actions, recrutement
// compris — la page Activité, le fil du tableau de bord et l'export CSV
// lisent la même table. Ce wrapper ne fait qu'y injecter TPL_META, qui
// vit côté client (libellés des 11 templates de rôle).
export function recActivityLabel(a: RecActivityDTO): string {
  return activityLabel(
    { action: a.action, payload: a.payload as Record<string, unknown> | null },
    { templateLabel: (k) => TPL_META[k]?.[0] },
  );
}

// ── le tableau de bord ──────────────────────────────────────
export interface RecTodo { ic: string; b: string; s: string; go: () => void }

export function RecDashboard({ meName, reqs, apps, activeApps, queue, obs, roles, activity, todos, canSal, canValidate, canEdit, onJump, onOpenApp, onNew }: {
  meName: string;
  reqs: RecRequisitionDTO[];
  apps: RecApplicationDTO[];
  activeApps: RecApplicationDTO[];
  queue: RecQueueItemDTO[];
  obs: RecOnboardingDTO[];
  roles: RecRoleDTO[];
  activity: RecActivityDTO[];
  todos: RecTodo[];
  canSal: boolean; canValidate: boolean; canEdit: boolean;
  onJump: (view: string) => void;
  onOpenApp: (appId: string) => void;
  onNew: () => void;
}) {
  const m = useMounted();
  const st = useRecSt();
  // v2 — candidats notés face à la barre, essais en cours.
  const barRows = activeApps.filter((a) => ['ENTRETIEN', 'FINALISTE', 'OFFRE'].includes(a.stage) && a.gates.jury?.overall != null && !a.gates.masked);
  const essais = obs.filter((o) => o.checkpoints.some((c) => !c.done));
  const openReqs = reqs.filter((r) => ['ouverte', 'validee'].includes(r.status));

  // Entretiens à venir: PLANIFIE des candidatures actives, par date.
  const itws: Array<{ app: RecApplicationDTO; itw: RecInterviewDTO }> = activeApps
    .flatMap((a) => a.interviews.filter((i) => i.status === 'PLANIFIE').map((itw) => ({ app: a, itw })))
    .sort((x, y) => x.itw.scheduledAt.localeCompare(y.itw.scheduledAt));

  // Scorecards en retard: entretiens réalisés où le jury n'est pas complet.
  const lateSc = activeApps.reduce(
    (n, a) => n + a.interviews.filter((i) => i.status === 'REALISE' && i.scorecardCount < i.interviewers.length).length,
    0,
  );

  // Offres en cours (version vivante, pas encore acceptée/refusée).
  const offRows = apps
    .map((a) => ({ a, o: recOfferOf(a), r: reqs.find((r) => r.id === a.requisitionId) }))
    .filter((x): x is { a: RecApplicationDTO; o: NonNullable<ReturnType<typeof recOfferOf>>; r: RecRequisitionDTO } =>
      x.o != null && x.r != null && !['ACCEPTEE', 'REFUSEE'].includes(x.o.status));

  // Funnel: candidatures actives par étape.
  const stages = REC_STAGES.map((s) => ({ ...s, n: activeApps.filter((a) => a.stage === s.id).length }));
  const tot = stages.reduce((s, x) => s + x.n, 0);

  const kpis: Array<[number, string, string, string, (() => void) | undefined]> = [
    [openReqs.length, 'Postes ouverts', 'var(--shf)', '🎯', () => onJump('pipeline')],
    [activeApps.length, 'Candidats actifs', '#0792a8', '👥', () => onJump('pipeline')],
    [itws.length, 'Entretiens à venir', '#2a6fdb', '🗓', () => onJump('pipeline')],
    [lateSc, 'Scorecards en retard', lateSc ? '#d8590f' : '#0a9d72', '⏱', () => onJump('pipeline')],
    [queue.length, 'À valider', queue.length ? '#c41d8f' : '#74747e', '🪪', canValidate ? () => onJump('valid') : undefined],
    [offRows.length, 'Offres en cours', '#d8590f', '✉️', () => onJump('offres')],
  ];

  const hello = canValidate
    ? 'Décisions en attente et vue d’ensemble du recrutement.'
    : canEdit
      ? `Votre pipeline vous attend — ${todos.length} action${todos.length > 1 ? 's' : ''} vous concerne${todos.length > 1 ? 'nt' : ''}.`
      : 'Vos entretiens et scorecards, rien d’autre.';
  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  let d = 0;
  const nd = () => { d += 80; return d; };

  return (
    <div>
      <div className="d11-hero">
        <span className="d11-hav" style={{ background: 'var(--shf)' }}>
          {meName.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
        </span>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="d11-hello">Bonjour <em>{meName.split(' ')[0]}</em> — {today}</div>
          <div className="d11-hsub">{hello}</div>
        </div>
        <div className="d11-hact">
          {canValidate && <button className="rec-ghost" onClick={() => onJump('valid')}>🪪 File de validation · {queue.length}</button>}
          <button className="rec-ghost" onClick={() => onJump('catalogue')}>🧬 Catalogue des rôles</button>
          {canEdit && <button className="rec-go" onClick={onNew}>＋ Nouvelle demande</button>}
        </div>
      </div>

      <div className="d11-kpis">
        {kpis.map(([v, l, c, ic, go], i) => <Kpi key={i} v={v} l={l} c={c} ic={ic} i={i} onGo={go} />)}
      </div>

      <div className="d11-grid">
        <Sec ic="⚡" c="var(--shf)" t="À traiter" k={todos.length} sub="personnalisé — selon vos droits" more={canValidate ? 'File de validation' : 'Pipeline'} onMore={() => onJump(canValidate ? 'valid' : 'pipeline')} sp={7} d={nd()}>
          {todos.map((t, i) => (
            <div key={i} className="d11-row" style={{ animationDelay: `${i * 60}ms` }} onClick={t.go}>
              <span className="d11-todo-ic">{t.ic}</span>
              <div style={{ minWidth: 0 }}><span className="tt">{t.b}</span><span className="ss">{t.s}</span></div>
              <span className="go">→</span>
            </div>
          ))}
          {!todos.length && <div className="rec-emptybox">✨ Rien en attente de votre côté.</div>}
        </Sec>

        <Sec ic="🎯" c="#e23b3b" t="Face à la barre" k={barRows.length} sub="chaque candidat comparé à la barre, jamais aux autres" more="Comparatif" onMore={() => onJump('pipeline')} sp={5} d={nd()}>
          <div className="rv2-thr" style={{ padding: '0 0 10px' }}>
            <div><b>{fmt1(st.barOverall)}</b><span>global min.</span></div>
            <div><b>{fmt1(st.barPerf)}</b><span>performance min.</span></div>
            <div><b>{fmt1(st.barMin)}</b><span>plancher dimension</span></div>
          </div>
          {barRows.map((a, i) => (
            <div key={a.id} className="rv2-barline" style={{ animationDelay: `${i * 60}ms` }} onClick={() => onOpenApp(a.id)}>
              <span className="rec-av sm" style={{ background: a.candidate.avColor }}>{a.candidate.firstName[0]}{a.candidate.lastName[0]}</span>
              <div className="nm"><b>{a.candidate.firstName} {a.candidate.lastName}</b><span>{a.stage === 'FINALISTE' ? 'Finaliste' : a.stage === 'OFFRE' ? 'Offre' : 'Entretien'} · {fmt1(a.gates.jury!.overall)}/5</span></div>
              <DimMeter s={a.gates.jury!} st={st} sm />
              <BarPill bar={a.gates.bar} compact overridden={a.gates.overridden} st={st} />
            </div>
          ))}
          {!barRows.length && <div className="rec-emptybox">Aucun candidat noté pour l’instant.</div>}
        </Sec>

        <Sec ic="🗓" c="#d8590f" t="Périodes d’essai" k={essais.length} sub="points J+15 → décision écrite avant la fin" more="Onboarding & essai" onMore={() => onJump('onboarding')} sp={7} d={nd()}>
          {essais.map((ob, i) => {
            const day = obDay(ob);
            const next = ob.checkpoints.find((c) => !c.done);
            const stt = next ? cpState(next, day) : null;
            return (
              <div key={ob.id} className="d11-row" style={{ animationDelay: `${i * 60}ms` }} onClick={() => onJump('onboarding')}>
                <span className="rec-av sm" style={{ background: ob.candidate.avColor }}>{ob.candidate.firstName[0]}{ob.candidate.lastName[0]}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <span className="tt">{ob.candidate.firstName} {ob.candidate.lastName} <span style={{ fontWeight: 600, color: 'var(--ink3)' }}>· {ob.roleLabel}</span></span>
                  <span className="ss">{day < 0 ? `J−${-day}` : `J+${day}`} · {next ? `${next.lbl} — ${stt!.lbl}` : 'tous les points faits'}</span>
                </div>
                <div style={{ width: 170 }}><ProbationStrip ob={ob} /></div>
              </div>
            );
          })}
          {!essais.length && <div className="rec-emptybox">Aucune période d’essai suivie.</div>}
        </Sec>

        <Sec ic="🗓" c="#2a6fdb" t="Entretiens à venir" k={itws.length} sub="prochains entretiens planifiés" more="Pipeline" onMore={() => onJump('pipeline')} sp={5} d={nd()}>
          {itws.slice(0, 6).map(({ app, itw }, i) => {
            const dt = new Date(itw.scheduledAt);
            return (
              <div key={itw.id} className="d11-row" style={{ animationDelay: `${i * 60}ms` }} onClick={() => onOpenApp(app.id)}>
                <span className="d11-cal"><b>{dt.getDate()}</b><span>{MO[dt.getMonth()]}</span></span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <span className="tt">{app.candidate.firstName} {app.candidate.lastName}</span>
                  <span className="ss">{itw.type} · {dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} · {itw.mode} · jury {itw.interviewers.map((j) => j.name.split(' ')[0]).join(', ')}</span>
                </div>
                <span className="go">→</span>
              </div>
            );
          })}
          {!itws.length && <div className="rec-emptybox">Aucun entretien planifié.</div>}
        </Sec>

        <Sec ic="📊" c="#0792a8" t="Santé du pipeline" k={tot} sub="toutes candidatures actives, par étape" more="Pipeline" onMore={() => onJump('pipeline')} sp={7} d={nd()}>
          {tot > 0 && (
            <div className="d11-bar">
              {stages.filter((s) => s.n > 0).map((s) => (
                <i key={s.id} title={`${s.lbl} — ${s.n}`} style={{ width: m ? `${(s.n / tot) * 100}%` : '0%', background: s.c }}></i>
              ))}
            </div>
          )}
          <div className="d11-leg">
            {stages.filter((s) => s.n > 0).map((s) => (
              <button key={s.id} className="d11-lchip" style={{ '--c': s.c } as React.CSSProperties} onClick={() => onJump('pipeline')}>
                <i></i>{s.lbl}<b>{s.n}</b>
              </button>
            ))}
          </div>
          <div style={{ borderTop: '1px dashed var(--line)', margin: '6px 0 2px' }}></div>
          {openReqs.map((r, i) => {
            const ra = activeApps.filter((a) => a.requisitionId === r.id);
            const ent = entOf(r.entityId);
            return (
              <div key={r.id} className="d11-row" style={{ animationDelay: `${i * 60}ms` }} onClick={() => onJump('pipeline')}>
                <div style={{ minWidth: 0, width: 190 }}>
                  <span className="tt">{r.roleLabel}</span>
                  <span className="ss">
                    {ent && <RecEnt id={ent.id} name={ent.name} color={ent.color} />}
                    {r.openedAt ? ` · ouvert depuis ${recDays(r.openedAt.slice(0, 10))} j` : ' · à publier'}
                  </span>
                </div>
                <div className="d11-mini">
                  {REC_STAGES.map((s) => {
                    const n = ra.filter((a) => a.stage === s.id).length;
                    return n ? <i key={s.id} title={`${s.lbl} — ${n}`} style={{ width: m ? `${(n / Math.max(ra.length, 1)) * 100}%` : '0%', background: s.c }}></i> : null;
                  })}
                </div>
                <span className="d11-cnt">{ra.length}<i>candidats</i></span>
                <span className="go">→</span>
              </div>
            );
          })}
          {!openReqs.length && <div className="rec-emptybox">Aucun poste ouvert dans ce périmètre.</div>}
        </Sec>

        <Sec ic="✉️" c="#d8590f" t="Offres en cours" k={offRows.length} sub="chaque version repasse par directeur → owner" more="Offres" onMore={() => onJump('offres')} sp={5} d={nd()}>
          {offRows.map(({ a, o, r }, i) => {
            const st = recOfferStOf(o.status);
            const dl = recOfferDaysLeft(o);
            return (
              <div key={o.id} className="d11-row" style={{ animationDelay: `${i * 60}ms` }} onClick={() => onJump('offres')}>
                <RecAv app={a} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <span className="tt">{a.candidate.firstName} {a.candidate.lastName} <span className="rec-vchip">v{o.version}</span></span>
                  <span className="ss">{r.roleLabel} · {canSal && o.salaire != null ? recFmtMoney(o.salaire) : '🔒 montant restreint'}</span>
                </div>
                {dl != null && <span className={`r6-dl ${dl <= 3 ? 'hot' : 'okk'}`}>{dl >= 0 ? `J−${dl}` : 'dépassée'}</span>}
                <span className="d11-st" style={{ '--stc': st.c } as React.CSSProperties}>{st.lbl}</span>
                <span className="go">→</span>
              </div>
            );
          })}
          {!offRows.length && <div className="rec-emptybox">Aucune offre dans ce périmètre.</div>}
        </Sec>

        <Sec ic="🧳" c="#0a9d72" t="Onboarding en cours" k={obs.length} sub="dossier admin + plan J1 → J90" more="Onboarding" onMore={() => onJump('onboarding')} sp={6} d={nd()}>
          {obs.map((ob, i) => {
            const dj = -obDays(ob);
            const dpct = ob.dossier.length ? Math.round(((ob.dossier.length - obDocsMissing(ob)) / ob.dossier.length) * 100) : 0;
            const tpct = obPct(ob);
            const ent = entOf(ob.entityId);
            return (
              <div key={ob.id} className="d11-row" style={{ animationDelay: `${i * 60}ms` }} onClick={() => onJump('onboarding')}>
                <span className="rec-av" style={{ background: ob.candidate.avColor }}>{ob.candidate.firstName[0]}{ob.candidate.lastName[0]}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <span className="tt">{ob.candidate.firstName} {ob.candidate.lastName}</span>
                  <span className="ss">
                    {ob.roleLabel} · {ent && <RecEnt id={ent.id} name={ent.name} color={ent.color} />} · {dj > 0 ? `démarre J−${dj}` : 'en poste'}
                  </span>
                </div>
                <div className="d11-pbs">
                  <span className="d11-pb">dossier<span className="tr"><i style={{ width: m ? `${dpct}%` : '0%', background: dpct === 100 ? '#0a9d72' : '#d8590f' }}></i></span><b>{dpct}%</b></span>
                  <span className="d11-pb">plan<span className="tr"><i style={{ width: m ? `${tpct}%` : '0%', background: 'var(--sc)' }}></i></span><b>{tpct}%</b></span>
                </div>
                <span className="go">→</span>
              </div>
            );
          })}
          {!obs.length && <div className="rec-emptybox">Aucun onboarding en cours — il se crée à l’acceptation d’une offre.</div>}
        </Sec>

        <Sec ic="🧬" c="#c41d8f" t="Catalogue des rôles" k={roles.length} sub="templates hérités par chaque recrutement" more="Ouvrir le catalogue" onMore={() => onJump('catalogue')} sp={6} d={nd()}>
          {roles.map((r, i) => {
            const ready = r.tplReady.filter((t) => t.ready).length;
            const pct = Math.round((ready / Math.max(r.tplReady.length, 1)) * 100);
            const open = reqs.filter((q) => q.roleId === r.id && ['ouverte', 'validee'].includes(q.status)).length;
            // bands === null → champ restreint (lock); {} → à définir.
            const band = r.bands ? r.bands[Object.keys(r.bands)[0] ?? ''] ?? null : null;
            const bandEl = r.bands == null ? <RecSal band={null} />
              : band ? <RecSal band={band} /> : <span>fourchette à définir</span>;
            return (
              <div key={r.id} className="d11-row" style={{ animationDelay: `${i * 60}ms` }} onClick={() => onJump('catalogue')}>
                <span className="d11-todo-ic">{GLYPH[r.dept] ?? '📁'}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <span className="tt">{r.label}</span>
                  <span className="ss">
                    {r.dept} · {open} poste{open > 1 ? 's' : ''} ouvert{open > 1 ? 's' : ''} · {bandEl}
                  </span>
                </div>
                <Ring pct={pct} c={pct === 100 ? '#0a9d72' : pct >= 70 ? '#d8590f' : '#e23b3b'} />
                <span className="go">→</span>
              </div>
            );
          })}
        </Sec>

        <Sec ic="🕘" c="#74747e" t="Activité récente" sub="journal d'audit — chaque action est tracée" sp={12} d={nd()}>
          <div className="d11-feed">
            {activity.slice(0, 10).map((a, i) => (
              <div key={i} className="d11-fr" style={{ animationDelay: `${i * 40}ms` }}>
                <span className="t">{a.at.slice(5, 10).split('-').reverse().join('/')}</span>
                <span className="d"></span>
                <p>{recActivityLabel(a)} — <b>{a.by}</b></p>
              </div>
            ))}
            {!activity.length && <div className="rec-emptybox">Aucune activité pour l’instant.</div>}
          </div>
        </Sec>
      </div>
    </div>
  );
}
